import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LabelList } from 'recharts';

const FeedbackScoreChart = ({ data, description }) => {
  // Colors for chart
  const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#06b6d4', '#3b82f6', '#6366f1'];

  // Data preparation for chart
  const recommendationScoreData = () => {
    const scoreData = [
      { name: '0 - Not at all likely', value: 0 },
      { name: '1 - Very unlikely', value: 0 },
      { name: '2 - Unlikely', value: 0 },
      { name: '3 - Somewhat unlikely', value: 0 },
      { name: '4 - Neutral', value: 0 },
      { name: '5 - Neutral', value: 0 },
      { name: '6 - Somewhat likely', value: 0 },
      { name: '7 - Likely', value: 0 },
      { name: '8 - Very likely', value: 0 },
      { name: '9 - Extremely likely', value: 0 },
      { name: '10 - Extremely likely', value: 0 }
    ];
    
    data.forEach(feedbackItem => {
      // Use npsScore (recommendation score) instead of feedbackScore
      const score = feedbackItem.npsScore || feedbackItem.averageScore || 0;
      if (score >= 0 && score <= 10) {
        scoreData[score].value++;
      }
    });
    
    return scoreData.filter(item => item.value > 0);
  };

  const chartData = recommendationScoreData();
  const totalResponses = chartData.reduce((sum, item) => sum + item.value, 0);

  // Calculate summary metrics
  const getScoreSummary = () => {
    const detractors = chartData.filter(item => {
      const score = parseInt(item.name.split(' ')[0]);
      return score >= 0 && score <= 6;
    }).reduce((sum, item) => sum + item.value, 0);
    
    const passives = chartData.filter(item => {
      const score = parseInt(item.name.split(' ')[0]);
      return score >= 7 && score <= 8;
    }).reduce((sum, item) => sum + item.value, 0);
    
    const promoters = chartData.filter(item => {
      const score = parseInt(item.name.split(' ')[0]);
      return score >= 9 && score <= 10;
    }).reduce((sum, item) => sum + item.value, 0);
    
    return { detractors, passives, promoters };
  };

  // Custom label function for pie slices
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value }) => {
    if (percent < 0.05) return null; // Don't show labels for slices smaller than 5%
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Custom legend formatter
  const renderLegend = (props) => {
    const { payload } = props;
    return (
      <div className="flex flex-col space-y-1 text-xs">
        {payload.map((entry, index) => {
          const percentage = ((entry.payload.value / totalResponses) * 100).toFixed(1);
          return (
            <div key={index} className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              ></div>
              <span className="text-gray-700">
                {entry.payload.name}: {entry.payload.value} ({percentage}%)
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className="dashboard-card p-6 slide-in-up" style={{animationDelay: '1200ms'}}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800">Satellite Recommendation Score</h3>
          <div className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-1 rounded-full">
            No Data
          </div>
        </div>
        {description && (
          <p className="text-sm text-gray-600 mb-4">{description}</p>
        )}
        <div className="flex items-center justify-center h-64 text-gray-500">
          <p>No recommendation data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-card p-6 slide-in-up" style={{animationDelay: '1200ms'}}>
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Satellite Recommendation Score</h3>
        <div className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full">
          Recommendation Likelihood
        </div>
      </div>
      {description && (
        <p className="text-sm text-gray-600 mb-4">{description}</p>
      )}
      
      {/* Summary Statistics */}
      {chartData.length > 0 && (
        <div className="mb-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-red-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-red-600">{getScoreSummary().detractors}</div>
              <div className="text-xs text-red-500">Detractors (0-6)</div>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-yellow-600">{getScoreSummary().passives}</div>
              <div className="text-xs text-yellow-500">Passives (7-8)</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-green-600">{getScoreSummary().promoters}</div>
              <div className="text-xs text-green-500">Promoters (9-10)</div>
            </div>
          </div>
        </div>
      )}
      
      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <p>No recommendation scores available</p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row items-center">
          <div className="w-full lg:w-2/3">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={100}
                  fill="#8884d8"
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((_, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]} 
                      stroke="rgba(255,255,255,0.8)"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: '8px', 
                    border: 'none', 
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    fontSize: '12px'
                  }}
                  formatter={(value, name) => [
                    `${value} responses (${((value / totalResponses) * 100).toFixed(1)}%)`, 
                    'Count'
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="w-full lg:w-1/3 lg:pl-4 mt-4 lg:mt-0">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3">Score Breakdown</h4>
              {renderLegend({ payload: chartData.map((item, index) => ({ 
                ...item, 
                color: COLORS[index % COLORS.length],
                payload: item
              })) })}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <strong>Total Responses:</strong> {totalResponses}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackScoreChart;
